import { getListingDetailsNavigationPath } from 'app/library/course-catalog-listing/utils/listing-details-navigation.util';
import { normalizeProfileBookingRequestStatus } from 'app/main/pages/profile/profile-form/profile-form-bookings-tab/utils/profile-booking-request-status.utils';
import { ICatalogListingBookingDetailsQuery } from '../interfaces/catalog-listing-booking-details.query.interface';
import { MaxbrainImage } from 'app/library/image-library/models/maxbrain-image.model';
import { IModuleDuration } from 'app/library/module/interfaces/module-duration.interface';
import { ModuleUserProgressStatus } from 'app/library/module-user/enums/module-user-progress-status.enum';
import { LandingPageComponentBookedExecution } from 'app/library/landing-page-component/models/landing-page-component-booked-execution.model';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserExtendedAttributeWithValue } from 'app/projects/user/src/lib/models/user-attribute-with-value';
import { CatalogListingBookingApprover } from './catalog-listing-booking-approver.model';
import { CatalogListingBookingTransfer } from './catalog-listing-booking-transfer.model';

export class CatalogListingBookingDetails {
    id: string;
    status: string;
    bookingProgressStatus: ModuleUserProgressStatus | null;
    experienceName: string;
    listingName: string;
    listingContentType: { id: number; name: string; displayNameKeySingular: string } | null;
    listingImage: MaxbrainImage | null;
    listingStartDate: string | null;
    listingEndDate: string | null;
    listingDuration: IModuleDuration | null;
    listingPrice: { amount: number | null; currency: string } | null;
    bookingRequestPrice: { amount: number | null; currency: string } | null;
    bookingRequestPreviousPrice: { amount: number | null; currency: string } | null;
    listingRegistrationSettings: { registrationEndDate: string | null; openSpots: number | null } | null;
    listingAttributes: UserExtendedAttributeWithValue[];
    cancelReason: string | null;
    requesterComment: string | null;
    canCancel: boolean;
    canDecide: boolean;
    canManageApprovers: boolean;
    createdAt: string;
    approvers: CatalogListingBookingApprover[];
    learnerId: number | null;
    learnerFullName: string;
    learnerImageUrl: string | null;
    requesterId: number | null;
    requesterFullName: string;
    requesterImageUrl: string | null;
    transferredTo: CatalogListingBookingTransfer | null;
    transferredFrom: CatalogListingBookingTransfer | null;
    bookedExecution: LandingPageComponentBookedExecution | null;
    canVisitExecution: boolean;

    constructor(private readonly _query: ICatalogListingBookingDetailsQuery) {
        this.id = _query.id.toString();
        this.status = normalizeProfileBookingRequestStatus(_query.status) || _query.status;
        this.bookingProgressStatus = _query.booking_progress_status ?? null;
        this.experienceName = _query.experience.name;
        this.listingName = _query.listing.name;
        this.listingContentType = _query.listing.course_offering.content_type
            ? {
                  id: _query.listing.course_offering.content_type.id,
                  name: _query.listing.course_offering.content_type.name,
                  displayNameKeySingular: _query.listing.course_offering.content_type.display_name_key_singular,
              }
            : null;
        this.listingImage = _query.course_details?.image ? new MaxbrainImage(_query.course_details.image) : null;
        this.listingStartDate = _query.listing.start_date ?? null;
        this.listingEndDate = _query.listing.end_date ?? null;
        this.listingDuration = this._mapDuration(_query.listing.duration);
        this.bookingRequestPrice = _query.price ?? null;
        this.bookingRequestPreviousPrice = _query.previous_price ?? null;
        this.listingPrice = _query.price ?? null;
        this.listingRegistrationSettings = _query.listing.registration_settings
            ? {
                  registrationEndDate: _query.listing.registration_settings.registration_end_date,
                  openSpots: _query.listing.registration_settings.open_spots,
              }
            : null;
        this.listingAttributes = _query.listing.attributes.map(
            (attributeWithValue) => new UserExtendedAttributeWithValue(attributeWithValue.attribute, attributeWithValue.value)
        );
        this.transferredTo = _query.transferred_to ? new CatalogListingBookingTransfer(_query.transferred_to) : null;
        this.transferredFrom = _query.transferred_from ? new CatalogListingBookingTransfer(_query.transferred_from) : null;
        this.bookedExecution = this._mapBookedExecution(_query);
        this.canVisitExecution = _query.can_visit_execution;
        this.cancelReason = _query.cancel_reason ?? null;
        this.requesterComment = _query.requester_comment ?? null;
        this.canCancel = !!_query.can_cancel;
        this.canDecide = !!_query.can_decide;
        this.canManageApprovers = !!_query.can_manage_approvers;
        this.createdAt = _query.created_at;
        this.approvers = (_query?.approvers || []).map((approver) => new CatalogListingBookingApprover(approver));

        const learner = _query?.user;
        this.learnerId = learner?.id ?? null;
        this.learnerFullName = [learner?.first_name, learner?.last_name].filter((value) => !!value).join(' ');
        this.learnerImageUrl = learner?.profile_picture?.thumb_url || learner?.profile_picture?.small_url || learner?.profile_picture?.url || null;

        const requester = _query?.requester || _query?.user;
        this.requesterId = requester?.id ?? null;
        this.requesterFullName = [requester?.first_name, requester?.last_name].filter((value) => !!value).join(' ');
        this.requesterImageUrl = requester?.profile_picture?.thumb_url || requester?.profile_picture?.small_url || requester?.profile_picture?.url || null;
    }

    getListingDetailsPath(user?: Pick<User, 'permissions' | 'catalogId'> | null, options?: { isSelfPaced?: boolean; canManageTeamBookings?: boolean }): string | null {
        return getListingDetailsNavigationPath(
            {
                courseOfferingId: this._query.listing.course_offering.id,
                listingId: this._query.listing.id,
                catalogUrl: this._query.course_details?.catalog_url ?? null,
                isSelfPaced: options?.isSelfPaced ?? this.bookedExecution?.selfPaced ?? this._isSelfPacedListing(),
                canManageTeamBookings: options?.canManageTeamBookings ?? this._canManageTeamBookings(),
            },
            user
        );
    }

    getTransferredListingDetailsPath(user?: Pick<User, 'permissions' | 'catalogId'> | null, options?: { isSelfPaced?: boolean; canManageTeamBookings?: boolean }): string | null {
        return getListingDetailsNavigationPath(
            {
                courseOfferingId: this._query.listing.course_offering.id,
                listingId: this.transferredTo?.listingId ?? null,
                catalogUrl: this._query.course_details?.catalog_url ?? null,
                isSelfPaced: options?.isSelfPaced ?? this.bookedExecution?.selfPaced ?? this._isSelfPacedListing(),
                canManageTeamBookings: options?.canManageTeamBookings ?? this._canManageTeamBookings(),
            },
            user
        );
    }

    private _mapBookedExecution(query: ICatalogListingBookingDetailsQuery): LandingPageComponentBookedExecution | null {
        if (query.booked_execution) {
            return new LandingPageComponentBookedExecution(query.booked_execution);
        }

        const listing = query.listing;
        if (!listing.linked_execution_details) {
            return null;
        }

        const linkedExecutionDetails = listing.linked_execution_details;

        return new LandingPageComponentBookedExecution({
            id: linkedExecutionDetails.id,
            name: listing.name,
            status: '',
            kind: linkedExecutionDetails.kind,
            self_paced: linkedExecutionDetails.self_paced,
            image: listing.image,
            version: listing.version,
            type: linkedExecutionDetails.type,
        });
    }

    private _isSelfPacedListing(): boolean {
        const listingType = this._query.listing.type.toLowerCase();
        return listingType.includes('self') || listingType.includes('e_learning') || listingType.includes('elearning');
    }

    private _canManageTeamBookings(): boolean {
        return !!this._query.can_decide || !!this._query.can_manage_approvers;
    }

    private _mapDuration(duration: IModuleDuration | null): IModuleDuration | null {
        if (!duration) {
            return null;
        }

        let hasValue = false;
        for (const property in duration) {
            if (duration[property as keyof IModuleDuration]) {
                hasValue = true;
                break;
            }
        }

        return hasValue ? duration : null;
    }
}
